// src/pages/MicrophoneCheckDialog.jsx
import { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  Chip,
} from "@mui/material";
import '../styles/microphoneCheckDialog.css';
import { useTranslation } from "react-i18next";

const OCTAVE_CENTERS = [500, 1000, 2000, 4000, 8000];
const SQRT2 = Math.SQRT2;

// ANSI S3.1 maximum permissible ambient noise levels
// Insert earphones, test range 500–8000 Hz, testing to 0 dB HL
// TODO: This should be updated. We think the limits should come directly
//   from the table present in the README, as they are already in dB SPL.
//   They may need to be different depending on the calibration of the
//   microphone, however.
const OSHA_LIMITS_DB_SPL = {
  500: 50,
  1000: 47,
  2000: 49,
  4000: 50,
  8000: 56,
};

const DEFAULT_SCAN_SECONDS = 10;
const DEFAULT_SUSTAIN_FAIL_SECONDS = 2;

// REW-calibrated SPL offset (matched against reference meter)
const DEFAULT_SPL_OFFSET_DB = 69.2;

// iMM-6C frequency response corrections (from CMM30685.cal)
const IMM6C_CAL_CORRECTIONS_DB = {
  500: 0.4,
  1000: 0.0,
  2000: -1.0,
  4000: 0.6,
  8000: 1.9,
};

function octaveEdges(fc) {
  return { f1: fc / SQRT2, f2: fc * SQRT2 };
}

// Returns the average power level (dB) across the frequency band.
// Averaging matches how the REW splOffsetDb calibration was derived
// (single-bin meter reference), so averaging here is correct.
function bandDbFromFreqData(freqDataDb, sampleRate, f1, f2) {
  const nyquist = sampleRate / 2;
  const binCount = freqDataDb.length;
  const hzPerBin = nyquist / binCount;

  const startBin = Math.max(0, Math.floor(f1 / hzPerBin));
  const endBin = Math.min(binCount - 1, Math.ceil(f2 / hzPerBin));

  // Allow single-bin bands (endBin === startBin is valid)
  if (endBin < startBin) return -Infinity;

  let pSum = 0;
  let n = 0;

  for (let i = startBin; i <= endBin; i++) {
    const db = freqDataDb[i];

    // Skip non-finite values (e.g. -Infinity from silent bins)
    if (!Number.isFinite(db)) continue;

    pSum += Math.pow(10, db / 10);
    n++;
  }

  if (n === 0 || pSum <= 0) return -Infinity;

  const pAvg = pSum / n;
  return 10 * Math.log10(pAvg);
}

export function MicrophoneCheckDialog({
  open,
  onClose,
  onPassed,
  scanSeconds = DEFAULT_SCAN_SECONDS,
  sustainFailSeconds = DEFAULT_SUSTAIN_FAIL_SECONDS,
  splOffsetDb = DEFAULT_SPL_OFFSET_DB,
  limitsDbSpl = OSHA_LIMITS_DB_SPL,
}) {
  const [status, setStatus] = useState("idle"); // idle | scanning | ok | fail
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [estimatedDb, setEstimatedDb] = useState(null);

  const { t } = useTranslation();

  const isCypress = localStorage.getItem('isCypress') === 'true';

  const audioRef = useRef(null);
  const trackersRef = useRef({
    startTs: null,
    lastTs: null,
    exceedMs: 0,
  });

  const cleanupAudio = async () => {
    const a = audioRef.current;
    audioRef.current = null;

    if (a?.rafId) cancelAnimationFrame(a.rafId);
    if (a?.stream) a.stream.getTracks().forEach((t) => t.stop());

    if (a?.source) {
      try { a.source.disconnect(); } catch { }
    }

    if (a?.analyser) {
      try { a.analyser.disconnect(); } catch { }
    }

    if (a?.ctx) {
      try { await a.ctx.close(); } catch { }
    }
  };

  const startScan = async () => {
    setError("");
    setStatus("scanning");
    setProgress(0);
    setEstimatedDb(null);

    trackersRef.current.startTs = performance.now();
    trackersRef.current.lastTs = null;
    trackersRef.current.exceedMs = 0;

    try {
      await cleanupAudio();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      const source = ctx.createMediaStreamSource(stream);

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 4096;
      analyser.smoothingTimeConstant = 0.2;
      analyser.minDecibels = -120;
      analyser.maxDecibels = 0;
      source.connect(analyser);

      const freqData = new Float32Array(analyser.frequencyBinCount);
      audioRef.current = { ctx, analyser, source, stream, rafId: null, freqData };

      const tick = (ts) => {
        const tr = trackersRef.current;

        if (!tr.lastTs) tr.lastTs = ts;
        const dtMs = ts - tr.lastTs;
        tr.lastTs = ts;

        const elapsedMs = ts - tr.startTs;
        setProgress(Math.min(100, (elapsedMs / (scanSeconds * 1000)) * 100));

        analyser.getFloatFrequencyData(freqData);

        let anyTooLoud = false;
        let maxBandDb = -Infinity;

        for (const fc of OCTAVE_CENTERS) {
          const { f1, f2 } = octaveEdges(fc);
          const bandDbfs = bandDbFromFreqData(freqData, ctx.sampleRate, f1, f2);

          if (!Number.isFinite(bandDbfs)) continue;

          const micCorrectionDb = IMM6C_CAL_CORRECTIONS_DB[fc] ?? 0;
          const bandDbSpl = bandDbfs + micCorrectionDb + splOffsetDb;

          if (bandDbSpl > maxBandDb) maxBandDb = bandDbSpl;

          const limit = limitsDbSpl[fc];
          if (limit != null && bandDbSpl > limit) {
            anyTooLoud = true;
          }
        }

        setEstimatedDb(Number.isFinite(maxBandDb) ? maxBandDb : null);

        if (anyTooLoud) tr.exceedMs += dtMs;
        else tr.exceedMs = 0;

        if (tr.exceedMs >= sustainFailSeconds * 1000) {
          setStatus("fail");
          cleanupAudio();
          return;
        }

        if (elapsedMs >= scanSeconds * 1000) {
          setStatus("ok");
          cleanupAudio();
          return;
        }

        if (audioRef.current) {
          audioRef.current.rafId = requestAnimationFrame(tick);
        }
      };

      audioRef.current.rafId = requestAnimationFrame(tick);
    } catch (e) {
      setStatus("idle");
      setError(
        e?.name === "NotAllowedError"
          ? t('microphone.permissionDenied')
          : e?.message || t('microphone.startFailed')
      );
      cleanupAudio();
    }
  };

  const close = async () => {
    await cleanupAudio();
    setStatus("idle");
    setProgress(0);
    setEstimatedDb(null);
    onClose?.();
  };

  const accept = async () => {
    await cleanupAudio();
    onPassed?.();
  };

  useEffect(() => {
    if (!open) {
      cleanupAudio();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chipState = (() => {
    if (status === "fail") {
      return { label: t('microphone.statusTooLoud'), color: "error", variant: "filled" };
    }
    if (status === "ok") {
      return { label: t('common.ok'), color: "success", variant: "filled" };
    }
    if (status === "scanning") {
      return { label: t('microphone.statusChecking'), color: "default", variant: "outlined" };
    }
    return { label: t('microphone.statusReady'), color: "default", variant: "outlined" };
  })();

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle>{t('microphone.title')}</DialogTitle>

      <DialogContent className="microphone-check-dialog__content">
        <Typography className="microphone-check-dialog__intro">
          {t('microphone.instruction')}
        </Typography>

        {error && (
          <Alert severity="error" className="microphone-check-dialog__error">
            {error}
          </Alert>
        )}

        <Box className="microphone-check-dialog__statusBox">
          <Typography variant="body2">
            {t('microphone.noiseLevel', { value: estimatedDb == null ? "—" : `~${estimatedDb.toFixed(0)} dB FS`, limit: '40 dB SPL' })}
            {/* For now, the limit can be hard-coded as 40 dB SPL because that is the minimum limit from the maximum permissible ambient noise levels table in the documentation. */}
          </Typography>

          <Chip
            size="small"
            label={chipState.label}
            color={chipState.color}
            variant={chipState.variant}
          />
        </Box>

        {status === "scanning" && (
          <Box className="microphone-check-dialog__progressWrap">
            <Typography variant="body2" className="microphone-check-dialog__progressText">
              {t('microphone.checkingPercent', { percent: Math.round(progress) })}
            </Typography>
            <LinearProgress value={progress} variant="determinate" />
          </Box>
        )}

        {status === "fail" && (
          <Alert severity="warning" className="microphone-check-dialog__fail">
            {t('microphone.failMessage')}
          </Alert>
        )}

        {status === "ok" && (
          <Alert severity="success" className="microphone-check-dialog__success">
            {t('microphone.okMessage')}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={close}>{t('common.cancel')}</Button>

        <Button variant="outlined" onClick={() => { isCypress ? setStatus("ok") : startScan() }} disabled={status === "scanning"} data-cy='room_noise_check_start'>
          {status === "scanning" ? t('microphone.checking') : t('microphone.start')}
        </Button>

        <Button variant="contained" onClick={accept} disabled={status !== "ok"} data-cy='room_noise_check_continue'>
          {t('common.continue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MicrophoneCheckDialog;
